import { EnvelopeSimple } from '@phosphor-icons/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { AuthCard, AuthCardHead } from '../components/auth/AuthCard';
import {
  AuthForm,
  AuthField,
  AuthFormError,
  AuthFormSuccess,
  authInputClassName,
} from '../components/auth/AuthForm';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { authAPI } from '../data/auth';
import pt from '../locales/pt';

const schema = z.object({
  email: z
    .string()
    .min(1, pt.auth.errors.emailRequired)
    .email(pt.auth.errors.emailInvalid),
});

export function ForgotPassword() {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (data) => {
    clearErrors('root.server');
    const response = await authAPI.requestPasswordReset(data.email);
    if (!response.success) {
      const message =
        typeof response.error === 'string'
          ? response.error
          : response.error?.email?.[0] ||
            response.error?.detail ||
            pt.auth.errors.generic;
      setError('root.server', { message });
    }
  };

  const success = isSubmitSuccessful && !errors.root?.server;

  return (
    <AuthCard badge={<EnvelopeSimple className="size-6" weight="regular" aria-hidden />}>
      <AuthCardHead
        title={success ? pt.auth.forgotPassword.successTitle : pt.auth.forgotPassword.title}
        subtitle={
          success ? pt.auth.forgotPassword.successSubtitle : pt.auth.forgotPassword.subtitle
        }
      />
      {success ? <AuthFormSuccess message={pt.auth.forgotPassword.successBanner} /> : null}
      <AuthForm onSubmit={handleSubmit(onSubmit)}>
        <AuthFormError message={errors.root?.server?.message} />
        <AuthField id="forgot-email" label={pt.auth.fields.email} error={errors.email?.message}>
          <Input
            id="forgot-email"
            type="email"
            autoComplete="email"
            invalid={!!errors.email}
            placeholder={pt.auth.fields.emailPlaceholder}
            className={authInputClassName}
            {...register('email')}
          />
        </AuthField>
        <Button type="submit" size="lg" loading={isSubmitting} className="w-full">
          {isSubmitting
            ? pt.common.loading
            : success
              ? pt.auth.forgotPassword.resubmit
              : pt.auth.forgotPassword.submit}
        </Button>
        <p className="text-center">
          <Link
            to="/sign-in"
            className="text-type-body-sm font-semibold text-sage-700 hover:underline dark:text-sage-400"
          >
            {pt.auth.forgotPassword.backToSignIn}
          </Link>
        </p>
      </AuthForm>
    </AuthCard>
  );
}
